import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Layout, Save, Loader2, Plus, Trash2, ChevronUp, ChevronDown, GripVertical, PanelTop, Menu as MenuIcon, Type, AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import { toast } from "sonner";

const TOPBAR_TYPES = [
  { v: "hours",    l: "Horaires du jour" },
  { v: "phone",    l: "Téléphone (cliquable)" },
  { v: "email",    l: "Email (cliquable)" },
  { v: "baseline", l: "Baseline de l'entreprise" },
  { v: "address",  l: "Adresse" },
  { v: "social",   l: "Icônes réseaux sociaux" },
  { v: "text",     l: "Texte libre" },
];

const CTA_STYLES = [
  { v: "sky",  l: "Bleu ciel" },
  { v: "red",  l: "Rouge" },
  { v: "navy", l: "Bleu marine" },
  { v: "ghost", l: "Bord seulement" },
];

const newMenuItem = () => ({ id: "m_" + Math.random().toString(36).slice(2, 8), label: "Nouvelle page", to: "/", show: true, children: [] });
const newChild = () => ({ label: "Sous-page", to: "/" });

export default function HeaderAdmin() {
  const { settings, refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setS({
        header_show_company_name: settings.header_show_company_name ?? true,
        header_show_baseline: settings.header_show_baseline ?? true,
        header_show_hours_pill: settings.header_show_hours_pill ?? true,
        header_show_account: settings.header_show_account ?? true,
        header_show_cart: settings.header_show_cart ?? true,
        header_menu_items: (settings.header_menu_items && settings.header_menu_items.length) ? settings.header_menu_items : [],
        header_cta_primary: settings.header_cta_primary || { enabled: true, label: "Devis gratuit", to: "/devis", style: "sky" },
        topbar_enabled: settings.topbar_enabled ?? false,
        topbar_bg: settings.topbar_bg || "#1E3A5F",
        topbar_fg: settings.topbar_fg || "#FFFFFF",
        topbar_items: settings.topbar_items || [],
        header_emergency_text: settings.header_emergency_text || "",
      });
    }
  }, [settings]);

  if (!s) return <div className="p-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Header enregistré");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  // --- Menu mutations ---
  const moveMenu = (i, dir) => {
    const arr = [...s.header_menu_items];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setS({ ...s, header_menu_items: arr });
  };
  const updateMenu = (i, patch) => setS({ ...s, header_menu_items: s.header_menu_items.map((m, idx) => idx === i ? { ...m, ...patch } : m) });
  const removeMenu = (i) => setS({ ...s, header_menu_items: s.header_menu_items.filter((_, idx) => idx !== i) });
  const addMenu = () => setS({ ...s, header_menu_items: [...s.header_menu_items, newMenuItem()] });

  const addChild = (i) => updateMenu(i, { children: [...(s.header_menu_items[i].children || []), newChild()] });
  const updateChild = (i, j, patch) => {
    const children = [...(s.header_menu_items[i].children || [])];
    children[j] = { ...children[j], ...patch };
    updateMenu(i, { children });
  };
  const removeChild = (i, j) => updateMenu(i, { children: (s.header_menu_items[i].children || []).filter((_, idx) => idx !== j) });

  // --- Topbar mutations ---
  const addTopItem = () => setS({ ...s, topbar_items: [...(s.topbar_items || []), { id: "t_" + Math.random().toString(36).slice(2, 8), type: "text", visible: true, align: "left", value: "" }] });
  const updateTopItem = (i, patch) => setS({ ...s, topbar_items: s.topbar_items.map((it, idx) => idx === i ? { ...it, ...patch } : it) });
  const removeTopItem = (i) => setS({ ...s, topbar_items: s.topbar_items.filter((_, idx) => idx !== i) });
  const moveTop = (i, dir) => {
    const arr = [...s.topbar_items];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setS({ ...s, topbar_items: arr });
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-header-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">Personnalisation</div>
        <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Header &amp; Navigation</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Choisissez ce qui apparaît dans l'en-tête, construisez votre menu avec des sous-pages, et activez un <em>topbar</em> desktop pour alléger la barre principale.
        </p>
      </header>

      {/* Visibilité des éléments */}
      <Card className="p-6 border space-y-3" data-testid="header-visibility-card">
        <h3 className="font-display font-bold flex items-center gap-2"><Layout className="w-4 h-4" /> Éléments affichés dans le header</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <ToggleRow label="Nom de l'entreprise" desc="Affiché à droite du logo" checked={s.header_show_company_name} onChange={v => setS({ ...s, header_show_company_name: v })} tid="t-company-name" />
          <ToggleRow label="Baseline (slogan)" desc="Sous le nom d'entreprise" checked={s.header_show_baseline} onChange={v => setS({ ...s, header_show_baseline: v })} tid="t-baseline" />
          <ToggleRow label="Pill horaires (desktop)" desc="Ouvert/Fermé avec statut temps réel" checked={s.header_show_hours_pill} onChange={v => setS({ ...s, header_show_hours_pill: v })} tid="t-hours" />
          <ToggleRow label="Icône compte client" desc="Uniquement si la boutique est activée" checked={s.header_show_account} onChange={v => setS({ ...s, header_show_account: v })} tid="t-account" />
          <ToggleRow label="Panier" desc="Uniquement si la boutique est activée" checked={s.header_show_cart} onChange={v => setS({ ...s, header_show_cart: v })} tid="t-cart" />
        </div>
      </Card>

      {/* CTA principal */}
      <Card className="p-6 border space-y-3" data-testid="header-cta-card">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold">Bouton d'action principal (CTA)</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold">Activer</span>
            <Switch checked={!!s.header_cta_primary.enabled} onCheckedChange={v => setS({ ...s, header_cta_primary: { ...s.header_cta_primary, enabled: v } })} data-testid="cta-primary-toggle" />
          </div>
        </div>
        <div className={`grid md:grid-cols-3 gap-3 ${s.header_cta_primary.enabled ? "" : "opacity-40 pointer-events-none"}`}>
          <div><Label>Libellé</Label><Input value={s.header_cta_primary.label || ""} onChange={e => setS({ ...s, header_cta_primary: { ...s.header_cta_primary, label: e.target.value } })} placeholder="ex: Devis gratuit" data-testid="cta-label" /></div>
          <div><Label>Lien / destination</Label><Input value={s.header_cta_primary.to || ""} onChange={e => setS({ ...s, header_cta_primary: { ...s.header_cta_primary, to: e.target.value } })} placeholder="/devis ou https://…" data-testid="cta-link" /></div>
          <div>
            <Label>Style</Label>
            <Select value={s.header_cta_primary.style || "sky"} onValueChange={v => setS({ ...s, header_cta_primary: { ...s.header_cta_primary, style: v } })}>
              <SelectTrigger data-testid="cta-style"><SelectValue /></SelectTrigger>
              <SelectContent>{CTA_STYLES.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Menu principal */}
      <Card className="p-6 border space-y-4" data-testid="header-menu-card">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold flex items-center gap-2"><MenuIcon className="w-4 h-4" /> Menu de navigation ({s.header_menu_items.length})</h3>
          <Button onClick={addMenu} className="bg-[color:var(--brand-red)]" data-testid="add-menu-item-btn"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
        </div>
        {s.header_menu_items.length === 0 ? (
          <div className="p-8 border-dashed border-2 rounded-xl text-center text-sm text-[hsl(var(--muted-foreground))]">
            Aucune entrée de menu. Ajoutez au moins "Accueil" ou "Contact".
          </div>
        ) : (
          <div className="space-y-2">
            {s.header_menu_items.map((m, i) => (
              <div key={m.id || i} className={`p-3 rounded-xl border ${m.show ? "bg-white" : "bg-[hsl(var(--muted))]/20 opacity-70"}`} data-testid={`menu-item-${i}`}>
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-[hsl(var(--muted-foreground))]/40 shrink-0" />
                  <Input value={m.label} onChange={e => updateMenu(i, { label: e.target.value })} placeholder="Libellé" className="h-9 font-semibold flex-1" />
                  <Input value={m.to} onChange={e => updateMenu(i, { to: e.target.value })} placeholder="/chemin" className="h-9 w-40 text-xs font-mono" />
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase">Visible</span>
                    <Switch checked={!!m.show} onCheckedChange={v => updateMenu(i, { show: v })} />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <button type="button" onClick={() => moveMenu(i, -1)} disabled={i === 0} className="w-6 h-5 rounded hover:bg-[hsl(var(--muted))] disabled:opacity-30 flex items-center justify-center"><ChevronUp className="w-3 h-3" /></button>
                    <button type="button" onClick={() => moveMenu(i, 1)} disabled={i === s.header_menu_items.length - 1} className="w-6 h-5 rounded hover:bg-[hsl(var(--muted))] disabled:opacity-30 flex items-center justify-center"><ChevronDown className="w-3 h-3" /></button>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => removeMenu(i)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
                {/* Children (sous-menu) */}
                <div className="mt-2 ml-6 space-y-1">
                  {(m.children || []).map((c, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">↳</span>
                      <Input value={c.label} onChange={e => updateChild(i, j, { label: e.target.value })} placeholder="Libellé sous-page" className="h-8 text-sm flex-1" />
                      <Input value={c.to} onChange={e => updateChild(i, j, { to: e.target.value })} placeholder="/chemin" className="h-8 w-40 text-xs font-mono" />
                      <Button variant="outline" size="sm" onClick={() => removeChild(i, j)} className="text-red-600 h-8 px-2"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addChild(i)} className="text-xs text-[color:var(--brand-sky)] font-semibold hover:underline ml-6">+ Ajouter une sous-page</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Topbar (desktop) */}
      <Card className="p-6 border space-y-4" data-testid="topbar-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold flex items-center gap-2"><PanelTop className="w-4 h-4" /> Topbar desktop</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              Barre fine au-dessus du header (desktop uniquement). Parfait pour déplacer les horaires, téléphone ou baseline afin de désengorger le header principal.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold">Activer</span>
            <Switch checked={!!s.topbar_enabled} onCheckedChange={v => setS({ ...s, topbar_enabled: v })} data-testid="topbar-toggle" />
          </div>
        </div>
        <div className={`${s.topbar_enabled ? "" : "opacity-40 pointer-events-none"} space-y-3`}>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Fond</Label><Input type="color" value={s.topbar_bg || "#1E3A5F"} onChange={e => setS({ ...s, topbar_bg: e.target.value })} className="h-10" data-testid="topbar-bg" /></div>
            <div><Label>Couleur du texte</Label><Input type="color" value={s.topbar_fg || "#FFFFFF"} onChange={e => setS({ ...s, topbar_fg: e.target.value })} className="h-10" data-testid="topbar-fg" /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--brand-navy)]">Éléments ({s.topbar_items.length})</div>
              <Button size="sm" variant="outline" onClick={addTopItem} data-testid="add-topbar-item-btn"><Plus className="w-3.5 h-3.5 mr-1" />Ajouter</Button>
            </div>
            <div className="space-y-2">
              {s.topbar_items.map((it, i) => (
                <div key={it.id || i} className="p-2 rounded-lg border flex items-center gap-2" data-testid={`topbar-item-${i}`}>
                  <Select value={it.type} onValueChange={v => updateTopItem(i, { type: v })}>
                    <SelectTrigger className="h-9 w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>{TOPBAR_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                  {it.type === "text" && (
                    <Input value={it.value || ""} onChange={e => updateTopItem(i, { value: e.target.value })} placeholder="Votre texte" className="h-9 flex-1" />
                  )}
                  <div className="flex items-center gap-1 ml-auto">
                    <button type="button" onClick={() => updateTopItem(i, { align: "left" })} className={`w-8 h-8 rounded ${it.align === "left" ? "bg-[color:var(--brand-navy)] text-white" : "hover:bg-[hsl(var(--muted))]"}`}><AlignLeft className="w-3.5 h-3.5 m-auto" /></button>
                    <button type="button" onClick={() => updateTopItem(i, { align: "center" })} className={`w-8 h-8 rounded ${it.align === "center" ? "bg-[color:var(--brand-navy)] text-white" : "hover:bg-[hsl(var(--muted))]"}`}><AlignCenter className="w-3.5 h-3.5 m-auto" /></button>
                    <button type="button" onClick={() => updateTopItem(i, { align: "right" })} className={`w-8 h-8 rounded ${it.align === "right" ? "bg-[color:var(--brand-navy)] text-white" : "hover:bg-[hsl(var(--muted))]"}`}><AlignRight className="w-3.5 h-3.5 m-auto" /></button>
                  </div>
                  <Switch checked={!!it.visible} onCheckedChange={v => updateTopItem(i, { visible: v })} />
                  <div className="flex flex-col gap-0.5">
                    <button type="button" onClick={() => moveTop(i, -1)} disabled={i === 0} className="w-6 h-5 rounded hover:bg-[hsl(var(--muted))] disabled:opacity-30 flex items-center justify-center"><ChevronUp className="w-3 h-3" /></button>
                    <button type="button" onClick={() => moveTop(i, 1)} disabled={i === s.topbar_items.length - 1} className="w-6 h-5 rounded hover:bg-[hsl(var(--muted))] disabled:opacity-30 flex items-center justify-center"><ChevronDown className="w-3 h-3" /></button>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => removeTopItem(i)} className="text-red-600"><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
              {s.topbar_items.length === 0 && (
                <div className="p-4 border-dashed border rounded-lg text-center text-xs text-[hsl(var(--muted-foreground))]">Aucun élément. Ajoutez horaires, téléphone, email, baseline…</div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Bandeau urgence */}
      <Card className="p-6 border space-y-3">
        <h3 className="font-display font-bold flex items-center gap-2"><Type className="w-4 h-4" /> Bandeau d'urgence (optionnel)</h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          Petit message rouge affiché TOUT en haut (ex: "Service dépannage 24h/24"). Laissez vide pour masquer.
        </p>
        <Input value={s.header_emergency_text || ""} onChange={e => setS({ ...s, header_emergency_text: e.target.value })} placeholder="ex: 🚨 Dépannage urgence 24h/24 — 01 23 45 67 89" />
      </Card>

      <div className="sticky bottom-0 py-4 bg-gradient-to-t from-[hsl(var(--background))] to-transparent">
        <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)] w-full md:w-auto" data-testid="save-header-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Enregistrer le header</>}
        </Button>
      </div>
    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange, tid }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border" data-testid={tid}>
      <Switch checked={!!checked} onCheckedChange={onChange} />
      <div>
        <div className="text-sm font-semibold text-[color:var(--brand-navy)]">{label}</div>
        <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{desc}</div>
      </div>
    </div>
  );
}
